import React, { useCallback } from "react";
import { useFlags } from "src/components/Providers/FeatureFlagProvider";
import * as FSR from "src/generated/hooks/form-service/requests";
import { useRemoteDataMutation } from "src/hooks/useRemoteDataMutation";
import useRankedSchools, {
  SchoolRankInsertInputType,
  UseSchoolRank,
} from "src/hooks/useSchoolRank";
import { triggerRuleExecutionsForSoftDeletedOffers } from "src/services/form/offer";
import * as GQL from "src/types/graphql";
import { SAVE_FORM_SCHOOLS_RANK_ADMIN } from "../View/EditForms/SchoolRankingEditForm/graphql/mutations";

export const useUpsertFormSchoolsRank = (
  initialRankedSchools: SchoolRankInsertInputType[]
) => {
  const rankedSchoolsHook = useRankedSchools(initialRankedSchools);
  const flags = useFlags(["form-service"]);
  const gqlHook = useGqlHook(rankedSchoolsHook);
  const restHook = useRestHook(rankedSchoolsHook);

  if (flags["form-service"].enabled) {
    return restHook;
  } else {
    return gqlHook;
  }
};

const useGqlHook = ({
  getUpsertedRanks,
  getDeletedRanks,
  getDeletedOffers,
  getDeletedWaitlists,
}: UseSchoolRank) => {
  const [isUpserting, setIsUpserting] = React.useState(false);

  const [updateRemoteData] = useRemoteDataMutation<
    GQL.SaveFormSchoolsRankAdmin,
    GQL.SaveFormSchoolsRankAdminVariables
  >(SAVE_FORM_SCHOOLS_RANK_ADMIN);

  // to keep the the function consistent with the rest hook,
  // we pass the formId and schoolRankingSectionId to the function.
  // But they are not used in the gql hook.
  const updateSchoolsRank = useCallback(
    async (
      _formId: string,
      _schoolRankingSectionId: string,
      rankedSchools: SchoolRankInsertInputType[]
    ) => {
      setIsUpserting(true);
      try {
        const upsertedSchoolRanks = getUpsertedRanks(rankedSchools);
        const deletedSchoolRanks = getDeletedRanks(upsertedSchoolRanks);
        const deletedOffers = getDeletedOffers(upsertedSchoolRanks);
        const deletedWaitlists = getDeletedWaitlists(upsertedSchoolRanks);

        const result = await updateRemoteData({
          variables: {
            upserted_school_ranks: upsertedSchoolRanks,
            deleted_school_ranks: deletedSchoolRanks,
            delete_offers_where: deletedOffers,
            delete_waitlists_where: deletedWaitlists,
          },
        });

        try {
          await FSR.createRuleExecutions({
            body: {
              data: {
                targets: upsertedSchoolRanks.map((schoolRank) => ({
                  type: "form_school" as const,
                  formId: schoolRank.form_id ?? "",
                  schoolId: schoolRank.school_id ?? "",
                })),
              },
            },
          });
        } catch (error) {
          console.warn("Error executing form rules:", error);
        }
        await triggerRuleExecutionsForSoftDeletedOffers({
          offers: result.data?.delete_offer?.returning ?? [],
          scope: "admin",
        });
      } finally {
        setIsUpserting(false);
      }
    },
    [
      getUpsertedRanks,
      getDeletedRanks,
      getDeletedOffers,
      getDeletedWaitlists,
      updateRemoteData,
    ]
  );

  return {
    updateSchoolsRank,
    isUpserting,
  };
};

const useRestHook = ({ getUpsertedRanks }: UseSchoolRank) => {
  const [isUpserting, setIsUpserting] = React.useState(false);

  const updateSchoolsRank = useCallback(
    async (
      formId: string,
      schoolRankingSectionId: string,
      rankedSchools: SchoolRankInsertInputType[]
    ) => {
      setIsUpserting(true);
      try {
        const upsertedSchoolRanks = getUpsertedRanks(rankedSchools);
        await FSR.putAnswers({
          path: {
            formId: formId,
          },
          body: {
            data: {
              [schoolRankingSectionId]: upsertedSchoolRanks.map(
                (schoolRank, index) => ({
                  schoolId: schoolRank.school_id ?? "",
                  rank: index,
                  isOverride: schoolRank.is_override ?? false,
                  overrideReason: schoolRank.override_reason ?? null,
                })
              ),
            },
          },
        });
      } finally {
        setIsUpserting(false);
      }
    },
    [getUpsertedRanks]
  );

  return {
    updateSchoolsRank,
    isUpserting,
  };
};
